/** Field ids match documentationSections / DocField missing() keys. */
export const FIELD_ERROR_COPY: Record<string, string> = {
  neuravue_confirmed: "Confirm the facts filled by NeuraVue.",
  resident_confirmed: "Confirm the resident.",
  event_time_confirmed: "Confirm the event time.",
  discovered_at_confirmed: "Confirm when staff discovered the event.",
  location_confirmed: "Confirm the location.",
  responders_confirmed: "Confirm who responded.",
  prefilled_confirmed: "Confirm that you reviewed each field filled by NeuraVue.",
  critical_incident_category_confirmed: "Confirm the incident category.",
  post_fall_instrument: "Confirm the post-fall assessment tool name and version.",
  post_fall_instrument_confirmed: "Confirm the post-fall assessment tool name and version.",
  post_fall_completed_at: "Record when the post-fall assessment was completed.",
  post_fall_protocol: "Use the post-fall schedule on file for this home.",
  significant_change: "Record whether there was a significant change in condition.",
  resident_identifier: "Record the room or the resident identifier.",
  province_confirmed: "Confirm the home's province. You cannot replace it on this record.",
  ai_policy: "This home has not confirmed an employer policy for AI-assisted documentation.",
  witnessed: "Record whether staff witnessed the event.",
  witnessed_by: "Record who witnessed it.",
  witnessed_title: "Record the title of the staff who witnessed it.",
  care_given: "Record the care given and who gave it.",
  emergency_care: "Record whether a physician or nurse practitioner gave emergency care.",
  safety_measures: "Record how you kept the resident safe.",
  intervention: "Record the intervention and how the resident responded.",
  hospital_transfer: "Record whether the resident was transferred to hospital.",
  hospital_name: "Record the hospital name.",
  hospital_time: "Record the time of the hospital transfer.",
  staff_removed_from_care: "Record whether the involved staff member was removed from this resident's care.",
  consciousness: "Record level of consciousness.",
  vitals: "Record vital signs and the time, or why they were not taken.",
  pain: "Record pain, or why it was not scored.",
  injuries: "Describe injuries, or choose none observed.",
  distress: "Record distress you saw, or none observed.",
  triggers: "Record triggers, or write none identified.",
  head_strike: "Record whether the resident's head was struck.",
  neuro_checks_started: "Record whether neuro checks were started.",
  neuro_checks_reason: "Record why neuro checks were not started.",
  environment_checked: "Record whether the environment was checked.",
  environment_note: "Record what you checked in the environment, or why it was not checked.",
  missing_last_seen: "Record when the resident was last seen.",
  missing_found_time: "Record when the resident was found.",
  missing_found_location: "Record where the resident was found.",
  observed: "Write what you observed, in your own words.",
  leading_events: "Record what led up to the event.",
  resident_statement: "Record the resident's own account, or that they could not say.",
  other_residents: "Name other residents involved, or write none.",
  persons_present: "Name the people who were present or who discovered it.",
  immediate_prevention: "Record what you did right away to prevent this happening again.",
  long_term_actions: "Record the longer-term actions planned.",
  care_plan_updated: "Record whether the plan of care was updated.",
  outcome: "Record how the people involved are now.",
  investigation_lead: "Name the investigation lead.",
  contributing_factors: "Record contributing factors, or none identified.",
  corrective_actions: "Record corrective actions and care-plan changes.",
  other_party_kind: "Record who the other person was.",
  other_resident_room: "Record the other resident's room.",
  in_care_plan: "Record whether this behaviour is already in the care plan.",
  suspected_abuse: "Record whether you suspect abuse or neglect.",
  conduct: "Choose what you saw so the regulatory category can be suggested.",
  first_aid_needed: "Record whether the injury needed first aid.",
  witness_residents: "Record residents who witnessed this, or write none.",
  restraint_used: "Record whether an emergency restraint was used.",
  criminal_offence: "Record whether a crime is possible.",
  evidence_preserved: "Record whether the clip is being held.",
  bc_category: "Confirm the regulatory category.",
  facility_record_confirmed: "Confirm this stays a facility record.",
  aga_reference: "Record the designated agency date and reference.",
  reasonable_grounds: "Record whether you have reasonable grounds.",
  suspicion: "Write the suspicion and the information it is based on.",
  qc_ah223: "Confirm whether an AH-223-1 declaration is required.",
  late_entry_reason: "This entry is late. Add the reason.",
  attestation_accepted: "Attest that you reviewed this record and it is accurate.",
  attestation_name: "Enter your name on the attestation.",
  attestation_designation: "Enter your designation.",
  notify_charge_nurse: "Record that the nurse in charge was told, including the time.",
  notify_physician: "Record that the physician or nurse practitioner was told, including the time.",
  notify_sdm_family: "Record that the family or substitute decision-maker was told, including the time.",
  notify_doc: "Record that the director of care or administrator was told, including the time.",
  notify_police: "Record the police notification, including the occurrence number.",
  notify_licensing: "Record the licensing notification.",
  notify_registry: "Record the registry notification.",
  notify_aga: "Record the designated agency notification.",
  notify_medical_director: "Record the medical director notification.",
  notify_actor: "Re-record notifications under your account, or have the person who recorded them finalize."
};

/** Map free-text finalize messages onto form field ids when possible. */
const MESSAGE_FIELD: Array<{ match: RegExp; field: string }> = [
  { match: /confirm the facts filled by neuravue|reviewed each field filled/i, field: "neuravue_confirmed" },
  { match: /confirm the resident/i, field: "resident_confirmed" },
  { match: /confirm the event time|record the event time/i, field: "event_time_confirmed" },
  { match: /discovered the event|when staff discovered/i, field: "discovered_at_confirmed" },
  { match: /confirm the location/i, field: "location_confirmed" },
  { match: /confirm who responded/i, field: "responders_confirmed" },
  { match: /incident category|regulatory category/i, field: "bc_category" },
  { match: /post-fall assessment tool name and version/i, field: "post_fall_instrument" },
  { match: /post-fall schedule on file|post-fall schedule|use the post-fall schedule/i, field: "post_fall_protocol" },
  { match: /post-fall assessment was completed/i, field: "post_fall_completed_at" },
  { match: /room or the resident identifier|does not store resident names/i, field: "resident_identifier" },
  { match: /confirm the home's province|province/i, field: "province_confirmed" },
  { match: /employer policy for ai-assisted/i, field: "ai_policy" },
  { match: /whether staff witnessed/i, field: "witnessed" },
  { match: /who witnessed|staff who witnessed|name of the staff who witnessed/i, field: "witnessed_by" },
  { match: /title of the staff who witnessed/i, field: "witnessed_title" },
  { match: /care given|immediate care given/i, field: "care_given" },
  { match: /emergency care/i, field: "emergency_care" },
  { match: /kept the resident safe|safety measures/i, field: "safety_measures" },
  { match: /intervention and/i, field: "intervention" },
  { match: /transferred to hospital/i, field: "hospital_transfer" },
  { match: /hospital transfer|where the resident was transferred|name the hospital/i, field: "hospital_name" },
  { match: /time of the hospital/i, field: "hospital_time" },
  { match: /removed from this resident's care/i, field: "staff_removed_from_care" },
  { match: /level of consciousness/i, field: "consciousness" },
  { match: /vital signs/i, field: "vitals" },
  { match: /record pain/i, field: "pain" },
  { match: /injuries/i, field: "injuries" },
  { match: /distress/i, field: "distress" },
  { match: /triggers/i, field: "triggers" },
  { match: /head was struck/i, field: "head_strike" },
  { match: /neuro checks were started/i, field: "neuro_checks_started" },
  { match: /why neuro checks were not/i, field: "neuro_checks_reason" },
  { match: /environment was checked/i, field: "environment_checked" },
  { match: /checked in the environment/i, field: "environment_note" },
  { match: /last seen|when they were found/i, field: "missing_last_seen" },
  { match: /what you observed|what you saw and heard/i, field: "observed" },
  { match: /led up to/i, field: "leading_events" },
  { match: /resident's own/i, field: "resident_statement" },
  { match: /other residents involved/i, field: "other_residents" },
  { match: /who was present|people who were present|persons present/i, field: "persons_present" },
  { match: /right away to prevent|immediate.*prevent/i, field: "immediate_prevention" },
  { match: /longer-term actions/i, field: "long_term_actions" },
  { match: /plan of care was updated|care plan were reviewed|care-plan changes|corrective actions/i, field: "care_plan_updated" },
  { match: /how the (people|resident) (involved )?are now|how the resident is now/i, field: "outcome" },
  { match: /investigation lead/i, field: "investigation_lead" },
  { match: /contributing factors/i, field: "contributing_factors" },
  { match: /who the other person was/i, field: "other_party_kind" },
  { match: /other resident's room/i, field: "other_resident_room" },
  { match: /already in the care plan/i, field: "in_care_plan" },
  { match: /suspect abuse/i, field: "suspected_abuse" },
  { match: /choose what you saw/i, field: "conduct" },
  { match: /needed first aid/i, field: "first_aid_needed" },
  { match: /residents who witnessed/i, field: "witness_residents" },
  { match: /emergency restraint/i, field: "restraint_used" },
  { match: /crime is possible/i, field: "criminal_offence" },
  { match: /clip is being held/i, field: "evidence_preserved" },
  { match: /facility record/i, field: "facility_record_confirmed" },
  { match: /designated agency/i, field: "aga_reference" },
  { match: /reasonable grounds/i, field: "reasonable_grounds" },
  { match: /suspicion and the information/i, field: "suspicion" },
  { match: /AH-223/i, field: "qc_ah223" },
  { match: /entry is late/i, field: "late_entry_reason" },
  { match: /attest that you reviewed/i, field: "attestation_accepted" },
  { match: /sign with your full name|name on the attestation|sign your own entry/i, field: "attestation_name" },
  { match: /designation/i, field: "attestation_designation" },
  { match: /nurse in charge/i, field: "notify_charge_nurse" },
  { match: /physician or nurse practitioner was told/i, field: "notify_physician" },
  { match: /family or substitute/i, field: "notify_sdm_family" },
  { match: /director of care or administrator/i, field: "notify_doc" },
  { match: /police notification/i, field: "notify_police" },
  { match: /notification to /i, field: "notify_charge_nurse" },
  { match: /notification was recorded by someone else/i, field: "notify_actor" },
  { match: /this entry is late/i, field: "late_entry_reason" },
  { match: /record the room or the resident identifier/i, field: "resident_identifier" }
];

export function fieldErrorCopy(field: string, fallback?: string): string {
  return FIELD_ERROR_COPY[field] || fallback || "This field is required.";
}

/** Build per-field errors from finalize messages + section missing ids. */
export function buildFieldErrors(messages: string[], missingFields: string[] = []): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of missingFields) {
    errors[field] = fieldErrorCopy(field);
  }
  for (const message of messages) {
    const hit = MESSAGE_FIELD.find((row) => row.match.test(message));
    if (hit) {
      errors[hit.field] = message;
      continue;
    }
    const notify = message.match(/notification to (.+?),/i);
    if (notify) {
      const role = notify[1].toLowerCase();
      const party =
        role.includes("physician") || role.includes("nurse practitioner")
          ? "physician"
          : role.includes("family") || role.includes("substitute")
            ? "sdm_family"
            : role.includes("director") || role.includes("administrator")
              ? "doc"
              : role.includes("police")
                ? "police"
                : role.includes("designated agency") || role.includes("guardianship")
                  ? "bc_aga"
                  : role.includes("licensing") || role.includes("health authority")
                    ? "licensing"
                    : role.includes("registry")
                      ? "registry"
                      : role.includes("charge") || role.includes("nurse in charge")
                        ? "charge_nurse"
                        : null;
      if (party) errors[`notify_${party}`] = message;
      else errors[`notify_${role.replace(/\s+/g, "_").slice(0, 40)}`] = message;
    }
  }
  return errors;
}

export const FINALIZE_TOAST =
  "Some required fields are missing or invalid. Fix the highlighted fields and try again.";
export const FINALIZE_TOAST_FR =
  "Des champs obligatoires sont manquants ou invalides. Corrigez les champs en surbrillance et réessayez.";
